import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ListChecks, ListTree } from 'lucide-react'
import { useSubTasks } from '../hooks/useSubTasks'
import { isClosedProgressType } from '../constants'
import type { StatusDocument } from '@/lib/db/rxdb/schemas/status'
import type { ChecklistItemDocument } from '@/lib/db/rxdb/hooks/entities/checklist-items'
import { useChecklistItems, useChecklists } from '@/lib/db/rxdb/hooks'

interface TaskProgressSummaryProps {
  taskId: string
  workspaceId: string | null | undefined
  statuses: Array<StatusDocument>
}

/**
 * Checklist and sub-task counts for the full page's property column (design 28a).
 * Renders nothing until the task has at least one of either.
 */
export function TaskProgressSummary({ taskId, workspaceId, statuses }: TaskProgressSummaryProps) {
  const { t } = useTranslation('tasks')
  const { checklists } = useChecklists({ taskId })
  const listIds = useMemo(
    () => checklists.filter((list) => !list.isDeleted).map((list) => list.id).sort(),
    [checklists],
  )
  // Only this task's items: the entity hook passes filter values straight into
  // the RxDB selector, so an $in over the task's checklist ids is scoped.
  const { checklistItems } = useChecklistItems(
    { checklistId: { $in: listIds } } as unknown as Partial<ChecklistItemDocument>,
    { enabled: listIds.length > 0 },
  )
  const { subTasks } = useSubTasks(taskId, workspaceId)

  const checklist = useMemo(() => {
    const items = listIds.length > 0 ? checklistItems.filter((item) => !item.isDeleted) : []
    return { done: items.filter((item) => item.isCompleted).length, total: items.length }
  }, [listIds, checklistItems])

  const subTaskCounts = useMemo(() => {
    const progressById = new Map(statuses.map((status) => [status.id, status.progressType]))
    const open = subTasks.filter((task) => !task.isDeleted)
    const done = open.filter((task) => isClosedProgressType(progressById.get(task.statusId)))
    return { done: done.length, total: open.length }
  }, [subTasks, statuses])

  if (checklist.total === 0 && subTaskCounts.total === 0) return null

  const row = (Icon: typeof ListChecks, label: string, counts: { done: number; total: number }) => (
    <span className="flex items-center gap-2">
      <Icon className="size-[13px] text-muted-foreground" />
      {label}
      <span className="flex-1" />
      <span className="font-mono text-[11.5px]">
        {counts.done}/{counts.total}
      </span>
    </span>
  )

  return (
    <div className="mt-4 border-t pt-4">
      <p className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
        {t('detailHeader.progress')}
      </p>
      <div className="flex flex-col gap-2 text-[12.5px] text-foreground/85">
        {checklist.total > 0 && row(ListChecks, t('detailHeader.checklist'), checklist)}
        {subTaskCounts.total > 0 && row(ListTree, t('detailHeader.subTasks'), subTaskCounts)}
      </div>
    </div>
  )
}
